// ============================================================
// ShellContext — single owner of right-panel state.
//
// Three modes — strict and non-negotiable: detail | comms | action.
// Selection is whatever was last passed to openDetail().
//
// Centralizing this here means views never reach into parent
// state — they call useShell().openDetail(item) and the
// MainframeShell handles the rest.
// ============================================================

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import {
  recordShellAction,
  setPanelMode as sentrySetPanelMode,
  setSelectionContext,
} from '../observability/sentry'

export type PanelMode = 'detail' | 'comms' | 'action'

export interface ShellContextValue {
  panelMode: PanelMode
  selected: unknown | null
  setPanelMode: (mode: PanelMode) => void
  openDetail: (item: unknown, mode?: PanelMode) => void
  clear: () => void
}

const ShellCtx = createContext<ShellContextValue | null>(null)

export function ShellProvider({ children }: { children: ReactNode }) {
  const [panelMode, setPanelModeState] = useState<PanelMode>('detail')
  const [selected, setSelected] = useState<unknown | null>(null)

  const setPanelMode = useCallback((mode: PanelMode) => {
    setPanelModeState(mode)
    sentrySetPanelMode(mode)
    recordShellAction('panel.mode_changed', { mode })
  }, [])

  const openDetail = useCallback((item: unknown, mode: PanelMode = 'detail') => {
    setSelected(item)
    setPanelModeState(mode)
    sentrySetPanelMode(mode)
    if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>
      const kind = inferSelectionKind(obj)
      setSelectionContext(kind, obj)
      recordShellAction('panel.open_detail', {
        kind,
        id: typeof obj.id === 'string' ? obj.id : undefined,
        // Tag the most useful identifier per kind, never the whole payload.
        actor: typeof obj.actor_id === 'string' ? obj.actor_id : undefined,
        severity: typeof obj.severity === 'string' ? obj.severity : undefined,
      })
    }
  }, [])

  const clear = useCallback(() => {
    setSelected(null)
    setPanelModeState('detail')
    sentrySetPanelMode('detail')
    setSelectionContext('project', null)
    recordShellAction('panel.cleared')
  }, [])

  const value = useMemo<ShellContextValue>(
    () => ({ panelMode, selected, setPanelMode, openDetail, clear }),
    [panelMode, selected, setPanelMode, openDetail, clear]
  )

  return <ShellCtx.Provider value={value}>{children}</ShellCtx.Provider>
}

function inferSelectionKind(obj: Record<string, unknown>): 'project' | 'event' | 'session' {
  if ('event_hash' in obj || 'chain_hash' in obj || 'event_type' in obj || 'severity' in obj) return 'event'
  if ('device_fingerprint' in obj || 'last_seen_at' in obj) return 'session'
  return 'project'
}

export function useShell(): ShellContextValue {
  const ctx = useContext(ShellCtx)
  if (!ctx) {
    // Defensive default — under SSR and outside the provider we
    // never want to throw and break the tree.
    return {
      panelMode: 'detail',
      selected: null,
      setPanelMode: () => {},
      openDetail: () => {},
      clear: () => {},
    }
  }
  return ctx
}
